import dayjs from 'dayjs';

// Material UI Components
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// Audit Vault Components
import ProgressBar from '../common/ProgressBar';

// Audit Vault Utilities
import { formatDate } from '../../utilities/common-date-utils';

export default function JobHistoryAccordion(props) {
  const {
    job,
    title,
    expanded,
    loading,
    history,
    page,
    rowsPerPage,
    onToggle,
    onPageChange,
    onRowsPerPageChange,
  } = props;

  return (
    <Accordion expanded={expanded} sx={{ mb: 1 }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${title.toLowerCase().replace(/\s+/g, '-')}-content`}
        id={`${title.toLowerCase().replace(/\s+/g, '-')}-header`}
        onClick={() => onToggle(job)}
      >
        <Box sx={{ width: '100%', pr: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              mb: 1,
              fontSize: { xs: '0.95rem', sm: '1rem' },
            }}
          >
            {title}:
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2">Active:</Typography>
                    {job.isActive ? <TaskAltIcon fontSize="small" sx={{ color: 'green' }} /> : <RadioButtonUncheckedIcon fontSize="small" />}
                </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2">Last Run Successful:</Typography>
                {job.isLastRunSuccessful ? (
                  <TaskAltIcon fontSize="small" sx={{ color: 'green' }} />
                ) : (
                  <ErrorOutlineIcon fontSize="small" sx={{ color: 'red' }} />
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                <b>Approx. Next Run Time:</b> {formatDate(dayjs(job.approxNextRunTime))}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <b>Items Processed During Last Run:</b> {job.auditEntriesProcessed}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
        <ProgressBar message="Loading ..." loading={loading} />

        <Typography
          sx={{
            mb: 1.5,
            fontSize: { xs: '0.9rem', sm: '1rem' },
          }}
        >
          Below is a listing of the dates and times this job has ran over the past month.
        </Typography>

        <TableContainer
          sx={{
            width: '100%',
            overflowX: 'auto',
          }}
        >
          <Table stickyHeader size="small" aria-label={`${title} history table`}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  }}
                >
                  Successful
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  }}
                >
                  Items Processed
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  }}
                >
                  Job Start Time
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  }}
                >
                  Job End Time
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {history
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((his) => (
                  <TableRow key={`${title}-history-${his.id}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {his.isLastRunSuccessful ? (
                        <TaskAltIcon fontSize="small" sx={{ color: 'green' }} />
                      ) : (
                        <ErrorOutlineIcon fontSize="small" sx={{ color: 'red' }} />
                      )}
                    </TableCell>

                    <TableCell sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                      {his.auditEntriesProcessed}
                    </TableCell>

                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                      }}
                    >
                      {formatDate(dayjs(his.lastJobRunStartTime))}
                    </TableCell>

                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.85rem', sm: '0.95rem' },
                      }}
                    >
                      {formatDate(dayjs(his.lastJobRunEndTime))}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {history.length > 25 && (
          <TablePagination
            rowsPerPageOptions={[25, 50, 75]}
            component="div"
            count={history.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            sx={{
              '.MuiTablePagination-toolbar': {
                px: { xs: 1, sm: 2 },
                flexWrap: 'wrap',
              },
              '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
              },
            }}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
}